import React, { useCallback, useEffect, useReducer, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import MessageBox from '../../components/MessageBox';
import { Skeleton } from '../../components/ui/skeleton';
import { BotonNegro, BonoCard, StepCard, PaymentMethodCard } from '../../components/lottery';
import { formatPaymentMethodTitle, paymentMethodDescription, paths } from '../../routes/paths';
import rifaService from '../../services/rifa.service';
import ticketService from '../../services/ticket.service';
import settingsService from '../../services/settings.service';
import paymentMethodService from '../../services/paymentMethod.service';
import { getError } from '../../utils';

/**
 * HomeScreen — redesigned to match the Figma "Inicio" frame.
 *
 * Sections (top→bottom):
 *  1. Hero carousel (configured via admin — slides with bg-image, title, subtitle, CTA)
 *  2. "Rifas activas" — live rifas from the API as minimal BonoCards
 *  3. "¿Como funciona?" — configurable 3-step cards
 *  4. "Metodos de pago" — active payment methods from API
 */

const rifaReducer = (state, action) => {
  switch (action.type) {
    case 'FETCH_REQUEST':
      return { ...state, loadingRifas: true };
    case 'FETCH_SUCCESS': {
      const list = Array.isArray(action.payload) ? action.payload : [];
      return { ...state, rifas: list, loadingRifas: false };
    }
    case 'FETCH_FAIL':
      return { ...state, loadingRifas: false, error: action.payload, rifas: [] };
    default:
      return state;
  }
};

/* ─── Hero carousel ────────────────────────────────────────────────────────── */
const DEFAULT_SLIDE = {
  title: '',
  subtitle: '',
  ctaLabel: '',
  ctaUrl: '/',
  imageUrl: '',
};

function HeroCarousel({ slides, onCtaClick }) {
  const { t } = useTranslation();
  const [current, setCurrent] = useState(0);
  const timerRef = useRef(null);

  const goTo = useCallback(
    idx => {
      setCurrent((idx + slides.length) % slides.length);
    },
    [slides.length]
  );

  // Auto-advance every 6 s when multiple slides
  useEffect(() => {
    if (slides.length <= 1) return;
    timerRef.current = setInterval(() => setCurrent(c => (c + 1) % slides.length), 6000);
    return () => clearInterval(timerRef.current);
  }, [slides.length]);

  const slide = slides[current] ?? DEFAULT_SLIDE;
  const title = slide.title || t('home.hero.title');
  const subtitle = slide.subtitle || t('home.hero.subtitle');
  const ctaLabel = slide.ctaLabel || t('home.hero.ctaLabel');

  const handleCta = () => {
    const url = slide.ctaUrl || '/';
    if (onCtaClick) onCtaClick(url);
  };

  return (
    <section className='relative w-full overflow-hidden' style={{ minHeight: '320px' }}>
      {/* Background */}
      <div
        className='absolute inset-0 bg-surface transition-all duration-700'
        style={
          slide.imageUrl
            ? {
                backgroundImage: `url(${slide.imageUrl})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
              }
            : undefined
        }
      />
      {/* Overlay for readability when image is present */}
      {slide.imageUrl && <div className='absolute inset-0 bg-black/40' />}

      {/* Content */}
      <div className='relative z-10 mx-auto flex max-w-3xl flex-col items-center justify-center px-6 py-14 text-center sm:py-20'>
        <h1
          className={`font-sans text-3xl font-bold sm:text-4xl ${
            slide.imageUrl ? 'text-white drop-shadow' : 'text-ink'
          }`}
        >
          {title}
        </h1>
        {subtitle && (
          <p
            className={`mt-3 text-base font-light ${
              slide.imageUrl ? 'text-white/85' : 'text-ink/70'
            }`}
          >
            {subtitle}
          </p>
        )}
        <div className='mt-8'>
          <BotonNegro onClick={handleCta}>{ctaLabel}</BotonNegro>
        </div>
      </div>

      {/* Prev / Next arrows (only when >1 slide) */}
      {slides.length > 1 && (
        <>
          <button
            onClick={() => goTo(current - 1)}
            className='absolute left-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white hover:bg-black/50 focus:outline-none'
            aria-label='Anterior'
          >
            <ChevronLeft className='h-5 w-5' />
          </button>
          <button
            onClick={() => goTo(current + 1)}
            className='absolute right-3 top-1/2 z-20 -translate-y-1/2 rounded-full bg-black/30 p-1.5 text-white hover:bg-black/50 focus:outline-none'
            aria-label='Siguiente'
          >
            <ChevronRight className='h-5 w-5' />
          </button>

          {/* Dot indicators */}
          <div className='absolute bottom-4 left-1/2 z-20 flex -translate-x-1/2 gap-1.5'>
            {slides.map((_, i) => (
              <button
                key={i}
                onClick={() => goTo(i)}
                className={`h-2 rounded-full transition-all focus:outline-none ${
                  i === current ? 'w-6 bg-white' : 'w-2 bg-white/50'
                }`}
                aria-label={`Slide ${i + 1}`}
              />
            ))}
          </div>
        </>
      )}
    </section>
  );
}

/* ─── Main component ────────────────────────────────────────────────────────── */
const DEFAULT_STEPS = [
  { label: 'Elegí tu bono.', description: '' },
  { label: 'Comprá tu ticket.', description: '' },
  { label: 'Listo! Ya estás colaborando.', description: 'Recibís tu comprobante por mail.' },
];
const STEP_NUMBERS = ['01.', '02.', '03.'];

export default function HomeScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const [{ error, rifas, loadingRifas }, dispatch] = useReducer(rifaReducer, {
    rifas: [],
    loadingRifas: true,
    error: '',
  });
  const [percentByRifa, setPercentByRifa] = useState({});

  // Home config (hero slides + how-it-works steps)
  const [heroSlides, setHeroSlides] = useState([DEFAULT_SLIDE]);
  const [howItWorksSteps, setHowItWorksSteps] = useState(DEFAULT_STEPS);

  // Active payment methods from API
  const [paymentMethods, setPaymentMethods] = useState([]);

  const itemsPerPage = 15;

  /* Fetch rifas */
  useEffect(() => {
    let cancelled = false;
    const fetchRifas = async () => {
      try {
        dispatch({ type: 'FETCH_REQUEST' });
        const raw = await rifaService.getRifas(0, itemsPerPage);
        const data = Array.isArray(raw) ? raw : Array.isArray(raw?.rifas) ? raw.rifas : [];
        if (data.length === 0) {
          if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', payload: [] });
          return;
        }
        const detailed = await Promise.all(
          data.map(async rifa => {
            const fullRifa = await rifaService.getRifa(rifa._id);
            try {
              const pct = await ticketService.getPorcentajeTicketsVendidos(rifa._id);
              if (!cancelled)
                setPercentByRifa(prev => ({ ...prev, [rifa._id]: pct?.vendidos || 0 }));
            } catch (_) {
              /* non-critical */
            }
            return fullRifa;
          })
        );
        if (!cancelled) dispatch({ type: 'FETCH_SUCCESS', payload: detailed });
      } catch (err) {
        if (!cancelled) dispatch({ type: 'FETCH_FAIL', payload: getError(err) });
      }
    };
    fetchRifas();
    return () => {
      cancelled = true;
    };
  }, []);

  /* Fetch home config (hero + steps) */
  useEffect(() => {
    settingsService
      .getHomeConfig()
      .then(data => {
        if (data?.heroSlides?.length) setHeroSlides(data.heroSlides);
        if (data?.howItWorksSteps?.length) setHowItWorksSteps(data.howItWorksSteps);
      })
      .catch(() => {
        /* use defaults */
      });
  }, []);

  useEffect(() => {
    paymentMethodService
      .getActiveMethods({ showInHome: true })
      .then(data => {
        if (Array.isArray(data) && data.length) setPaymentMethods(data);
      })
      .catch(() => {
        /* use static fallback below */
      });
  }, []);

  const goToRifa = rifa => {
    const route = rifa.tipo === 'board' ? paths.buyBoard(rifa._id) : paths.buyTickets(rifa._id);
    navigate(route, { state: { rifa } });
  };

  const handleHeroCta = url => {
    if (url && url !== '/' && url.startsWith('/')) {
      navigate(url);
    } else {
      document.getElementById('rifas-activas')?.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Fallback payment methods (static) when none configured in DB yet
  const staticPaymentMethods = [
    {
      _id: 'wire',
      method: t('home.paymentMethods.wireTitle'),
      homeDescription: t('home.paymentMethods.wireDescription'),
    },
    {
      _id: 'deposit',
      method: t('home.paymentMethods.depositTitle'),
      homeDescription: t('home.paymentMethods.depositDescription'),
    },
    {
      _id: 'mp',
      method: t('home.paymentMethods.mercadoPagoTitle'),
      homeDescription: t('home.paymentMethods.mercadoPagoDescription'),
    },
  ];
  const displayedPaymentMethods = paymentMethods.length ? paymentMethods : staticPaymentMethods;

  return (
    <div className='min-h-screen bg-white font-sans text-ink'>
      {/* ────── 1. HERO CAROUSEL ────── */}
      <HeroCarousel slides={heroSlides} onCtaClick={handleHeroCta} />

      {/* ────── 2. RIFAS ACTIVAS ────── */}
      <section id='rifas-activas' className='mx-auto w-full max-w-5xl px-6 pt-8'>
        <div className='flex items-center justify-between'>
          <h2 className='text-lg font-semibold text-ink'>{t('home.activeRaffles')}</h2>
        </div>

        <div id='rifas-grid' className='mt-4'>
          {loadingRifas ? (
            <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
              {Array.from({ length: 8 }).map((_, i) => (
                <div
                  key={i}
                  className='flex flex-col overflow-hidden rounded-xl bg-surface shadow-sm'
                >
                  {/* Image placeholder */}
                  <Skeleton className='aspect-[4/3] w-full rounded-none' />
                  {/* Body */}
                  <div className='flex flex-col gap-2 px-3 py-2.5'>
                    <Skeleton className='h-4 w-3/4' />
                    <Skeleton className='h-3 w-1/2' />
                    <Skeleton className='h-1.5 w-full rounded-full' />
                    <div className='flex items-center justify-between'>
                      <Skeleton className='h-3 w-10' />
                      <Skeleton className='h-4 w-8' />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : error ? (
            <MessageBox variant='danger'>{error}</MessageBox>
          ) : !rifas || rifas.length === 0 ? (
            <MessageBox variant='info'>{t('common.noResults')}</MessageBox>
          ) : (
            <div className='grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4'>
              {rifas.map(rifa => {
                const price =
                  rifa.ticketPrice ?? rifa.precio ?? rifa.price ?? rifa?.sorteos?.[0]?.precio;
                return (
                  <BonoCard
                    key={rifa._id}
                    title={rifa.nombre}
                    price={price}
                    percent={percentByRifa[rifa._id] || 0}
                    imageUrl={rifa.rifaImage?.fileLink}
                    onClick={() => goToRifa(rifa)}
                  />
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* ────── 3. ¿COMO FUNCIONA? ────── */}
      <section className='mx-auto w-full max-w-5xl px-6 pt-12'>
        <p className='text-lg font-semibold text-ink'>{t('home.howItWorks.kicker')}</p>
        <h3 className='mt-1 text-sm font-medium italic text-ink'>{t('home.howItWorks.title')}</h3>
        <div className='mt-4 grid grid-cols-3 gap-4'>
          {howItWorksSteps.slice(0, 3).map((step, i) => (
            <StepCard
              key={i}
              number={STEP_NUMBERS[i]}
              label={step.label}
              description={step.description}
            />
          ))}
        </div>
      </section>

      {/* ────── 4. METODOS DE PAGO ────── */}
      <section className='mx-auto w-full max-w-5xl px-6 pt-10 pb-16'>
        <h2 className='text-lg font-semibold text-ink'>{t('home.paymentMethods.title')}</h2>
        <div className='mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3'>
          {displayedPaymentMethods.map(pm => (
            <PaymentMethodCard
              key={pm._id}
              title={formatPaymentMethodTitle(pm)}
              description={paymentMethodDescription(pm)}
            />
          ))}
        </div>
      </section>
    </div>
  );
}
